import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Card, Alert, Container } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrash, FaPlus, FaBriefcase, FaGraduationCap, FaArrowLeft } from 'react-icons/fa';
import {
  createEmployment,
  updateEmployment,
} from '../actions/employmentActions';
import {
  createEducation,
  updateEducation,
} from '../actions/educationActions';
import GradientButton from './GradientButton';

function DescriptionForm({ backlink, nextlink, type = 'experience' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const data = location.state || {};
  const [points, setPoints] = useState(data.description || []);
  const [input, setInput] = useState('');

  const addPoint = () => {
    if (input.trim() !== '') {
      setPoints([...points, input.trim()]);
      setInput('');
    }
  };

  const removePoint = (indexToRemove) => {
    setPoints(points.filter((_, index) => index !== indexToRemove));
  };

  const capitalize = (str) =>
    !str ? '' : str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month] = dateString.split('-');
    return `${month}.${year}`;
  };

  const handleSubmit = () => {
    const start = data.start_date || data.startDate;
    const end = data.end_date || data.endDate;

    if (!start || (!data.currentlyWorking && !data.currentlyStudying && !end)) {
      alert('Uzupełnij daty przed zapisaniem.');
      return;
    }

    if (type === 'experience') {
      const payload = {
        id: data.id,
        position: data.position,
        employer_name: data.employer_name,
        city: data.city,
        start_date: `${start}-01`,
        end_date: data.currentlyWorking ? null : `${end}-01`,
        description: points,
      };

      if (payload.id) {
        dispatch(updateEmployment(payload));
      } else {
        dispatch(
          createEmployment(
            payload.position,
            payload.employer_name,
            payload.city,
            payload.start_date,
            payload.end_date,
            payload.description
          )
        );
      }
    } else if (type === 'education') {
      const payload = {
        id: data.id,
        school_name: data.school_name,
        city: data.city,
        start_date: `${start}-01`,
        end_date: data.currentlyStudying ? null : `${end}-01`,
        description: points,
      };

      if (payload.id) {
        dispatch(updateEducation(payload));
      } else {
        dispatch(
          createEducation(
            payload.school_name,
            payload.city,
            payload.start_date,
            payload.end_date,
            payload.description
          )
        );
      }
    }

    navigate(nextlink);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addPoint();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      overflowX: 'clip',
      width: '100%'
    }}>
      <Container fluid className="px-2 px-md-3">
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={8}>
            {/* HEADER */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-4"
            >
              <h2 className="text-white fw-bold mb-2">
                {type === 'experience'
                  ? 'Opisz swoje doświadczenie'
                  : 'Opisz swoją edukację'}
              </h2>
              <p className="text-white-50">
                Dodaj kluczowe obowiązki i osiągnięcia
              </p>
            </motion.div>

            {/* GŁÓWNA KARTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card 
                className="shadow-lg border-0"
                style={{ borderRadius: '20px', overflow: 'hidden', minWidth: '250px' }}
              >
                {/* HEADER KARTY */}
                <Card.Header 
                  style={{
                    background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                    border: 'none',
                    padding: '1.5rem'
                  }}
                >
                  <div className="text-white">
                    {type === 'experience' ? (
                      <>
                        <h5 className="mb-1 fw-bold">
                          <FaBriefcase className="me-2" />
                          {capitalize(data.position)}
                        </h5>
                        <p className="mb-1">{capitalize(data.employer_name)}</p>
                        <small className="opacity-75">
                          {capitalize(data.city)}, {formatDate(data.start_date)} -{' '}
                          {data.currentlyWorking
                            ? 'Obecnie'
                            : formatDate(data.end_date)}
                        </small>
                      </>
                    ) : (
                      <>
                        <h5 className="mb-1 fw-bold">
                          <FaGraduationCap className="me-2" />
                          {capitalize(data.school_name)}
                        </h5>
                        <small className="opacity-75">
                          {capitalize(data.city)}, {formatDate(data.start_date)} -{' '}
                          {data.currentlyStudying
                            ? 'Obecnie'
                            : formatDate(data.end_date)}
                        </small>
                      </>
                    )}
                  </div>
                </Card.Header>

                <Card.Body className="p-4">
                  {/* INPUT DO DODAWANIA */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium">
                      Dodaj punkt opisu
                    </Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        placeholder={
                          type === 'experience'
                            ? 'np. Obsługa maszyn produkcyjnych'
                            : 'np. Udział w projektach naukowych'
                        }
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        style={{
                          borderRadius: '10px',
                          border: '2px solid #e9ecef',
                          padding: '0.75rem'
                        }}
                      />
                      <Button
                        onClick={addPoint}
                        style={{
                          borderRadius: '10px',
                          border: 'none',
                          background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                          padding: '0.75rem 1.5rem',
                          minWidth: '60px',
                          transform: window.innerWidth >= 768 ? 'scale(1)' : 'scale(0.75)',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #0056b3 0%, var(--bs-primary) 100%)';
                          e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)';
                          e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.3)';
                        }}
                      >
                        <FaPlus />
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      <small>Naciśnij Enter lub kliknij + aby dodać</small>
                    </Form.Text>
                  </Form.Group>

                  {/* LISTA PUNKTÓW */}
                  {points.length > 0 ? (
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3">Dodane obowiązki:</h6>
                      <AnimatePresence>
                        {points.map((point, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.2 }}
                            className="mb-2"
                          >
                            <Card
                              className="border-0 shadow-sm"
                              style={{
                                borderRadius: '10px',
                                background: 'rgba(var(--bs-primary-rgb), 0.05)'
                              }}
                            >
                              <Card.Body className="p-3">
                                <Row className="align-items-center">
                                  <Col>
                                    <div className="d-flex align-items-start">
                                      <span className="me-2 text-primary fw-bold">•</span>
                                      <span style={{ fontSize: '0.95rem' }}>{point}</span>
                                    </div>
                                  </Col>
                                  <Col xs="auto">
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => removePoint(index)}
                                      style={{
                                        borderRadius: '8px',
                                        padding: '0.4rem 0.6rem'
                                      }}
                                    >
                                      <FaTrash size={14} />
                                    </Button>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Alert 
                      variant="info" 
                      className="border-0 text-center"
                      style={{ borderRadius: '15px' }}
                    >
                      <div style={{ fontSize: '2rem' }} className="mb-2">📝</div>
                      <small>
                        Dodaj przynajmniej jeden punkt opisu, aby lepiej zaprezentować swoje doświadczenie
                      </small>
                    </Alert>
                  )}
                </Card.Body>
              </Card>
              {/* PRZYCISKI NAWIGACJI */}
              <Row className="mt-2 g-3">
                <div className="d-flex justify-content-between">
                  <GradientButton
                    onClick={() =>
                      navigate(backlink, {
                        state: { ...data, description: points },
                      })
                    }
                    gradient="pink"
                  >
                    <FaArrowLeft className="me-2" />
                    Wstecz
                  </GradientButton>
                  <GradientButton
                    onClick={handleSubmit}
                    gradient="blue"
                  >
                    Zapisz
                  </GradientButton>
                </div>
              </Row>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default DescriptionForm;